import { defineStore } from "pinia";
import { CommonHttp, SingleIdRequest } from "@/models/interfaces/http/common.http.request";
import { reactive, shallowReactive } from "vue";
import { rootStore } from "../index";
import { CommonEntity, SingleStringResponse } from "@/models/interfaces/common";
import { CommonEntityRecordList, StorageItemExpenditureMovementCreateRequest, StorageItemExpensePriceSetRequest, StorageItemIncomeCreateRequest, 
  StorageItemIncomeEditRequest, StorageItemMovementToProjectAttachAsker, StorageMovementNoteAppendRequest,
  StorageNoteTemplateCreateRequest } from "@/models/interfaces/storage/storage-models";
import { StorageItemMovementInfo } from "../../models/interfaces/finances/finance-models";


/**
 * Методы АПИ движений позиций складов
 */

// API методы по версиям

// v 9.0.10.0
// getMovementProjects → GET api/storage/movements/GetProjects

// v 9.0.12.0
// getStorageMovementInfo → GET api/storage/movements/GetInfo/{movementId}

// v 9.2.2.0
// setExpensePrice → PUT api/storage/movements/SetExpensePrice

// v 12.0.5.0
// addFileToMovement → POST api/storage/movements/AddFile
// deleteFileFromMovement → DELETE api/storage/movements/File/{fileId}
// appendMovementNote → POST api/storage/movements/AppendNote

// v 12.1.3.0
// createMovementNoteTemplate → POST api/storage/movement-note-templates
// deleteMovementNoteTemplate → DELETE api/storage/movement-note-templates/{templateId}
// getMovementNoteTemplates → GET api/storage/movement-note-templates

// v 12.2.9.0
// attachMovementToProject → PUT api/storage/movements/AttachMovementToProject
// detachMovementFromProject → POST api/storage/movements/DetachMovementFromProject
// attachValuedMovementToProject → PUT api/storage/movements/AttachValuedMovementToProject
// detachValuedMovementFromProject → POST api/storage/movements/DetachValuedMovementFromProject

// v 12.5.2.0
// createStorageExpense → POST api/storage/movements/expense
// createStorageTransfer → POST api/storage/movements/transfer

// v 12.5.2.1
// createStorageIncome → POST api/storage/movements/income

// v 12.5.9.0
// editStorageIncome → PUT api/storage/movements/income

const API_URL = process.env.VUE_APP_API_URL

export type QueryState = {
    loading: boolean;
    // ignore the request result and don't mutate the model after the request is finished
    stop_request: boolean;

    // детальная информация о движении позиции товара.
    // movement_info: StorageItemMovementInfo;
    // список проектов для движения.
    // movement_projects: CommonEntityRecordList;
    // список шаблонов комментария движения позиции склада, отсортированный по частоте использования.
    // movement_note_templates: CommonEntityRecordList;
    // обновлённый комментарий движения позиции склада.
    movement_appended_note: SingleStringResponse;

    // новый шаблон комментария позиции склада.
    movement_note_template_created: CommonEntity;

    // asynchronous queue
    reqQueue: Map<string, AbortController>;
};

export const storageMovementsStore = defineStore("storageMovementsStore", {
  state: () =>
    ({
        loading: false,
        // ignore the request result and don't mutate the model after the request is finished
        stop_request: false,
        // reactive filters change and update directly from interface
        movement_info: shallowReactive({} as StorageItemMovementInfo),
        // movement_projects:  shallowReactive({} as CommonEntityRecordList),
        // movement_note_templates: shallowReactive({} as CommonEntityRecordList),
        movement_appended_note: shallowReactive({} as SingleStringResponse),

        movement_note_template_created: shallowReactive({} as CommonEntity),

        reqQueue: new Map()
    } as QueryState),

  actions: {

    // GET

    // GET movement projects
    async getMovementProjects(filter?: string, count?: number, offset?: number): Promise<CommonEntityRecordList | undefined> {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = 'movement_projects';

      // abort all other entity/graph result connections because only one element can be displayed at a time
      Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
        if (key === entkey) {
          console.log('abort conn '+key)
          this.reqQueue.get(key)?.abort();
          this.reqQueue.delete(key);
        }
      });

      const controller = new AbortController();
      this.reqQueue.set(entkey, controller);

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined

        const params: any = {}
        if (filter !== undefined) params.filter = filter
        if (count !== undefined) params.count = count
        if (offset !== undefined) params.offset = offset

        await http.get(`storage/movements/GetProjects`, { 
          signal: controller.signal,
          params 
        }).then(res => {
          response = res
          this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log(err);
          this.reqQueue.delete(entkey);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // this.movement_projects = reactive({} as CommonEntityRecordList);
          // reactive filters change and update directly from interface
          const { data } = response;
          // this.movement_projects = reactive(data);

          this.loading = false
          return data // { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return undefined // { response_ok: false }
    },

    // GET storage movement info
    async getStorageMovementInfo(movementId: number) {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = 'storage_movement_info';

      // abort all other entity/graph result connections because only one element can be displayed at a time
      Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
        if (key === entkey) {
          console.log('abort conn '+key)
          this.reqQueue.get(key)?.abort();
          this.reqQueue.delete(key);
        }
      });

      const controller = new AbortController();
      this.reqQueue.set(entkey, controller);

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.get(`storage/movements/GetInfo/${movementId}`, { signal: controller.signal }).then(res => {
          response = res
          this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log(err);
          this.reqQueue.delete(entkey);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // this.movement_info = reactive({} as StorageItemMovementInfo);
          // reactive filters change and update directly from interface
          const { data } = response;
          // this.movement_info = reactive(data);

          this.loading = false
          return reactive(data);
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return undefined
    },

    // GET movement note templates
    async getMovementNoteTemplates(value?: string, count?: number, offset?: number) {
      const rootstore = rootStore()
      this.stop_request = false

      // const entkey = 'movement_note_templates';

      // abort all other entity/graph result connections because only one element can be displayed at a time
      // Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
      //   if (key === entkey) {
      //     console.log('abort conn '+key)
      //     this.reqQueue.get(key)?.abort();
      //     this.reqQueue.delete(key);
      //   }
      // });

      // const controller = new AbortController();
      // this.reqQueue.set(entkey, controller);

      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined

        // const params: any = {}
        // if (value !== undefined) params.Value = value
        // if (count !== undefined) params.Count = count
        // if (offset !== undefined) params.Offset = offset

        await http.get(`storage/movement-note-templates?value=${value}&count=${count}&offset=${offset}`/*, { 
          signal: controller.signal,
          params 
        }*/).then(res => {
          response = res
          // this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log(err);
          // this.reqQueue.delete(entkey);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // this.movement_note_templates = reactive({} as CommonEntityRecordList);
          // reactive filters change and update directly from interface
          const { data } = response;
          // this.movement_note_templates = reactive(data);

          this.loading = false
          return reactive(data)
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return undefined
    },

    // EDIT

    // POST create storage income movement
    async createStorageIncome(arg: StorageItemIncomeCreateRequest) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/movements/income`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // reactive filters change and update directly from interface
          const { data } = response;

          this.loading = false
          return data
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return undefined
    },

    // POST create storage expense movement
    async createStorageExpense(arg: StorageItemExpenditureMovementCreateRequest) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/movements/expense`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // reactive filters change and update directly from interface
          const { data } = response;

          this.loading = false
          return data
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return undefined
    },

    // POST create storage transfer movement
    async createStorageTransfer(arg: StorageItemExpenditureMovementCreateRequest) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/movements/transfer`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // reactive filters change and update directly from interface
          const { data } = response;

          this.loading = false
          return data
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return undefined
    },

    // PUT edit storage income movement
    async editStorageIncome(arg: StorageItemIncomeEditRequest) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.put(`storage/movements/income`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // PUT attach movement to project
    async attachMovementToProject(arg: StorageItemMovementToProjectAttachAsker) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.put(`storage/movements/AttachMovementToProject`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // POST detach movement from project
    async detachMovementFromProject(arg: StorageItemMovementToProjectAttachAsker) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/movements/DetachMovementFromProject`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // PUT attach valued movement to project
    async attachValuedMovementToProject(arg: StorageItemMovementToProjectAttachAsker) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.put(`storage/movements/AttachValuedMovementToProject`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // POST detach valued movement from project
    async detachValuedMovementFromProject(arg: SingleIdRequest) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/movements/DetachValuedMovementFromProject`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // PUT set expense price
    async setExpensePrice(arg: StorageItemExpensePriceSetRequest) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.put(`storage/movements/SetExpensePrice`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // POST add file to movement comment
    async addFileToMovement(file: File, entityId: number) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT, false, true)
      try {
        this.loading = true
        let response: any = undefined

        const formData = new FormData()
        formData.append('File', file)
        formData.append('EntityId', entityId.toString())

        await http.post(`storage/movements/AddFile`, formData).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // reactive filters change and update directly from interface
          const { data } = response;

          this.loading = false
          return data
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return undefined
    },

    // DELETE file from movement comment
    async deleteFileFromMovement(fileId: number) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.delete(`storage/movements/File/${fileId}`).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // POST append note to movement
    async appendMovementNote(arg: StorageMovementNoteAppendRequest) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/movements/AppendNote`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.movement_appended_note = reactive({} as SingleStringResponse);
          // reactive filters change and update directly from interface
          const { data } = response;
          this.movement_appended_note = reactive(data);

          this.loading = false
          return { response_ok: true, data }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false, data: undefined }
    },

    // POST create movement note template
    async createMovementNoteTemplate(arg: StorageNoteTemplateCreateRequest) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.post(`storage/movement-note-templates`, arg).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.movement_note_template_created = reactive({} as CommonEntity);
          // reactive filters change and update directly from interface
          const { data } = response;
          this.movement_note_template_created = reactive(data);

          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    // DELETE movement note template
    async deleteMovementNoteTemplate(templateId: number) {
      const rootstore = rootStore()
      this.stop_request = false
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        this.loading = true
        let response: any = undefined
        await http.delete(`storage/movement-note-templates/${templateId}`).then(res => {
          response = res
        }).catch(err => {
          console.log(err);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          this.loading = false
          return { response_ok: true }
        } else {
          console.log('Error response status:', response?.status);
        }
      } catch (e) {
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    clearSettings() {
      // reactive filters change and update directly from interface
      this.clearCommon();
    },

    clearCommon() {
      // reactive filters change and update directly from interface
    },
  },

  getters: {
    
    isLoading(state): boolean {
      return state.loading
    }
  }
});